// Register settings for surcharge/NCA + debit cashback (Android SettingsModel + registerSettingsModel).
// Field names are mapped DEFENSIVELY — the live response shape isn't pinned here, so we accept the
// common variants and default to "off" when a field is absent (a non-surcharge merchant is unaffected).
// Also exposes the store `enable_order_number` flag (a separate store-options endpoint).

import { phpRegisterSettings, phpStoreOptions, phpStoreDetails, auth } from "./api";
import { roundHalfUp, toNum } from "../engine/money";
import { isDeziDiscountEnabled } from "../engine/dezi";

function pick(o, ...keys) {
  for (const k of keys) if (o && o[k] != null && o[k] !== "") return o[k];
  return undefined;
}

const regiFlags = (row) =>
  String(pick(row, "regi_setting", "regiSetting") ?? "")
    .split(",")
    .map((t) => t.trim());

const ageFlags = (row) =>
  String(pick(row, "age_verify", "ageVerify") ?? "")
    .split(",")
    .map((t) => t.trim());

const firstRow = (res) => {
  const body = res && (res.result || res.data || res);
  return (Array.isArray(body) ? body[0] : body) || {};
};

export async function getRegisterSettings() {
  let res;
  let storeRes = null;
  try {
    [res, storeRes] = await Promise.all([phpRegisterSettings(), phpStoreDetails().catch(() => null)]);
  } catch {
    return null;
  }
  const row = firstRow(res);
  // NCA / Dual Price live on user_options and reach the register only via Settingapi/store_details
  // (Android StoreDetailsEntityLocal). inventory_register_setting never carries them.
  const store = firstRow(storeRes);
  const surcharge = { ...row, ...store };
  return {
    // "0" off, "1" NCA, "2" dual price (Android StoreDetailsEntityLocal.isSurcharge)
    isSurcharge: String(pick(surcharge, "is_surcharge", "isSurcharge") ?? "0"),
    surchargePerCredit: toNum(pick(surcharge, "surcharge_per", "surchargePer")),
    surchargePerDebit: toNum(pick(surcharge, "debit_surcharge", "debitSurcharge", "surchargePerDebit")),
    cashbackChargeAmount: toNum(pick(row, "cashbackChargeAmount", "cashback_charge_amount", "cashback_charge", "cashback_fee")),
    enableCashbackLimit: String(pick(row, "enableCashbackLimit", "enable_cashback_limit", "enable_cashback") ?? "0") === "1",
    // Android cashbackLimitAmount — the most cash back one ORDER may give (inventory_setting.cashback_limit_amount)
    cashbackLimitAmount: toNum(pick(row, "cashback_limit_amount", "cashbackLimitAmount")),
    // QV-7124 dezi/mKonnekt gate. Both fields live on THIS endpoint (the backend moved them out of
    // the login response in 2026-07). Absence-safe: missing ⇒ "0"/"" ⇒ gate OFF ⇒ the whole
    // manufacturer-discount feature is inert, which is what protects every non-dezi store.
    isDeziEnabled: String(pick(row, "is_dezi_enabled", "isDeziEnabled") ?? "0"),
    rcn: String(pick(row, "rcn", "RCN") ?? ""),
    // `ebt` is a CSV of enabled payment options. Android reads flag "5" as **Disable Debit Card**
    // (QV-7233) — note the INVERSION: present ⇒ debit is DISABLED.
    ebtList: String(pick(row, "ebt", "ebtList") ?? "")
      .split(",")
      .map((t) => t.trim())
      .filter(Boolean),
    // Android `regiSetting.checkNumberExistsInSet(STOCK_PROMPT)` — flag "1" in the `regi_setting`
    // CSV: an out-of-stock add opens the "update quantity" prompt instead of a plain refusal.
    stockPrompt: String(pick(row, "regi_setting", "regiSetting") ?? "")
      .split(",")
      .map((t) => t.trim())
      .includes("1"),
    // Android InStorePaymentFragment ~9627: three more `regi_setting` flags that shape the Custom Item flow —
    // "8"  CUSTOM_CATEGORY      → custom item takes the DEFAULT tax (else the cashier picks taxes in a dialog)
    // "10" ALLOW_CUSTOM_ITEM_EBT → ask "Sell Custom Item As Food Stampable"
    // "11" CUSTOM_ITEM_CHECK_ID → custom item is age-restricted (21+ gate)
    customCategoryTax: regiFlags(row).includes("8"),
    allowCustomItemEbt: regiFlags(row).includes("10"),
    customItemCheckId: regiFlags(row).includes("11"),
    // "12" NUMPAD_PAYOUT → an amount keyed on the register numpad before Pay pre-fills the amount to
    //      pay on the payment screen (Android proceedToPayment → quickPayViewModel.setAmount).
    numpadPayout: regiFlags(row).includes("12"),
    // "5"  _21PLUS_MANAGER_PIN → the ID-scan dialog's "Manager Verify" opens the manager-PIN keypad
    //      (else it toasts "Please Checked Manager Pin From Setting").
    // "9"  HIDE_EXPECTED_CASH → hides Expected Cash on the POS close-shift screen (no web surface).
    managerPin: regiFlags(row).includes("5"),
    hideExpectedCash: regiFlags(row).includes("9"),
    // Android `ageVerify.checkNumberExistsInSet` on the `age_verify` CSV: "1" birthday prompt,
    // "2" expiry prompt. Both ⇒ DOB+expiry dialog; one ⇒ single prompt; neither ⇒ ID-scan dialog.
    ageVerifyBirthday: ageFlags(row).includes("1"),
    ageVerifyExpiry: ageFlags(row).includes("2"),
    // Android barcode_msg (QuickAddSettings "if barcode not scanned"): 1 display not found, 2 quick-add
    // forced, 3 quick-add prompt, 4 full-add forced, 5 full-add prompt (no full-add screen on web → 4≈2, 5≈3).
    barcodeMsg: String(pick(row, "barcode_msg", "barcodeMsg") ?? "1"),
    raw: row,
  };
}

// QV-7233 — "Disable Debit Card" store setting. Flag "5" in the `ebt` CSV means the merchant turned
// debit OFF, so the tile must be disabled. ⚠️ The sense is inverted relative to the other flags in
// that list ("1" food EBT / "2" cash EBT / "3" gift card / "4" lottery payout all ENABLE their tile).
export function isDebitCardDisabled(settings) {
  return !!settings && (settings.ebtList || []).includes("5");
}
// Android AmountToPayFragment.settingBaseButtons(): the `ebt` CSV ENABLES these tenders —
// "1" EBT Food, "2" EBT Cash, "3" Gift Card (and "4" lottery/scratcher payout elsewhere). Absent
// settings ⇒ every tile on, matching a POS whose settings have not loaded.
const ebtHas = (settings, flag) => !settings || !Array.isArray(settings.ebtList) || settings.ebtList.includes(flag);
export function ebtFoodEnabled(settings) {
  return ebtHas(settings, "1");
}
export function ebtCashEnabled(settings) {
  return ebtHas(settings, "2");
}
export function giftCardTenderEnabled(settings) {
  return ebtHas(settings, "3");
}

/** True when this store has the manufacturer-discount (Altria/mKonnekt) feature switched on. */
export function deziGateOn(settings) {
  return isDeziDiscountEnabled(settings && settings.isDeziEnabled, settings && settings.rcn);
}

// NCA/surcharge for a card tender: baseAmount × (credit|debit %) / 100, half-up. 0 unless enabled +
// the card is credit/debit (EBT never surcharged). Returns { amount, percent }.
// NCA mode ("1") ONLY: dual price ("2") carries the premium inside the card price itself, and
// Android zeroes the debit % outside mode "1" (setNonCashAdjustment) — see engine/dualPrice.
export function ncaFor(settings, baseAmount, cardType) {
  if (!settings || settings.isSurcharge !== "1") return { amount: 0, percent: 0 };
  const ct = String(cardType || "").toLowerCase();
  let pct;
  if (ct.includes("credit")) pct = settings.surchargePerCredit;
  else if (ct.includes("debit")) pct = settings.surchargePerDebit;
  else return { amount: 0, percent: 0 };
  if (!(pct > 0)) return { amount: 0, percent: 0 };
  return { amount: roundHalfUp((toNum(baseAmount) * pct) / 100), percent: pct };
}

// Debit cashback fee (flat charge) — only when cashback is enabled AND the tender is debit.
export function cashbackFeeFor(settings, cardType) {
  if (!settings || !settings.enableCashbackLimit) return 0;
  if (!String(cardType || "").toLowerCase().includes("debit")) return 0;
  return roundHalfUp(settings.cashbackChargeAmount);
}

export function cashbackEnabled(settings, cardType) {
  return !!(settings && settings.enableCashbackLimit && String(cardType || "").toLowerCase().includes("debit"));
}

/** Android getRoundOffList (UtilsMethod:3290): [exact, ⌊g+1⌋, ⌊next/10+1⌋×10, ⌊next/10+2⌋×10]. */
export function quickCashList(grand) {
  const g = Number(grand) || 0;
  if (!(g > 0)) return [];
  const next = Math.trunc(g + 1);
  return [roundHalfUp(g), next, Math.trunc(next / 10 + 1) * 10, Math.trunc(next / 10 + 2) * 10];
}

// Store `enable_order_number` flag — the web mirror of Android getStoreDetailsData().enableOrderNumber.
// saveOrderPos (Process_order) assigns a real sequential order_number ONLY when the payload sends
// order_number=0 AND enable_order_number=1; the web previously hardcoded the flag off, so every web
// order stayed order_number 0 while Android orders got numbered. Memoized per merchant (a store switch
// reloads the register, but key defensively). Returns "0" on any lookup failure so an outage never
// blocks the sale — the server just leaves the order unnumbered, matching flag-off behaviour.
let _eonCache = { merchantId: null, value: null };

export async function getEnableOrderNumber() {
  const { merchantId } = auth();
  if (_eonCache.merchantId === merchantId && _eonCache.value != null) return _eonCache.value;
  let flag = "0";
  try {
    const res = await phpStoreOptions();
    const ud = (res && (res.user_data || res.userData)) || {};
    flag = String(ud.enable_order_number ?? "0") === "1" ? "1" : "0";
  } catch {
    flag = "0";
  }
  _eonCache = { merchantId, value: flag };
  return flag;
}
